/**
 * Browser download mechanics, shared by every surface that hands the user a
 * file the server rendered on demand.
 *
 * The rule these exist to enforce: **an endpoint that renders on demand can
 * refuse, so never point a browser tab at one.** `window.open(url)` and a bare
 * anchor both paint the refusal's JSON body as a page (or drop a `.json` into
 * Downloads) and give the calling code nothing to read, so the caller cannot
 * tell a refusal from a success. Fetch first, read the status, and download the
 * resolved bytes.
 *
 * Lifted out of `components/distribution/declarations/download-utils.ts`
 * (LF-6106, which met this on the declaration download) when the institution
 * form download needed the same shape (VALE-1513). That file re-exports these
 * so its own callers are unaffected.
 */

/**
 * Same-origin attachment download without leaving the current page.
 *
 * `download=''` lets the server's `Content-Disposition` filename win on a
 * same-origin URL; pass `filename` for a blob URL, which carries no headers.
 */
export function triggerBrowserDownload(url: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename ?? '';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Download an already-fetched body. */
export function triggerBlobDownload(blob: Blob, filename?: string): void {
  const objectUrl = URL.createObjectURL(blob);
  triggerBrowserDownload(objectUrl, filename);
  // Revoke on the next macrotask — revoking synchronously after `click()` can
  // abort the download before the browser has taken the blob.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * Pull the filename out of a `Content-Disposition` header, so a blob download
 * keeps the name the backend chose rather than defaulting to the object-URL
 * UUID.
 */
export function filenameFromContentDisposition(
  header: string | null
): string | undefined {
  if (!header) return undefined;

  // RFC 5987 `filename*` wins when present: the backend emits it alongside an
  // ASCII-folded `filename`, and it is the one that survives non-ASCII names.
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Malformed percent-encoding — fall through to the ASCII form.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : undefined;
}

/**
 * The sentence a refused download should show the user.
 *
 * A domain refusal carries a `message` written for the lawyer, naming what is
 * wrong and what to do about it. That sentence is the whole value of the
 * refusal, so it is what surfaces; `fallback` covers a transport failure or a
 * body that carries no message of its own.
 */
export async function readRefusalMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'message' in body) {
      const { message } = body as { message?: unknown };
      if (typeof message === 'string' && message.trim()) return message;
      // Nest renders a validation failure's `message` as an array of strings.
      if (Array.isArray(message) && typeof message[0] === 'string') {
        return message.join(' ');
      }
    }
  } catch {
    // Not JSON, or an empty body — the fallback is the honest answer.
  }
  return fallback;
}
